import React from 'react';
import { clsx } from 'clsx';
import { CheckCircle, AlertCircle, XCircle, Info, X } from 'lucide-react';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({
    className,
    variant = 'info',
    title,
    children,
    dismissible = false,
    onDismiss,
    ...props
  }, ref) => {
    const variants = {
      success: {
        container: 'bg-success/8 border-success/20 text-text-primary',
        icon: CheckCircle,
        iconColor: 'text-success',
      },
      error: {
        container: 'bg-error/8 border-error/20 text-text-primary',
        icon: XCircle,
        iconColor: 'text-error',
      },
      warning: {
        container: 'bg-warning/8 border-warning/20 text-text-primary',
        icon: AlertCircle,
        iconColor: 'text-warning',
      },
      info: {
        container: 'bg-info/8 border-info/20 text-text-primary',
        icon: Info,
        iconColor: 'text-info',
      },
    };

    const { container, icon: Icon, iconColor } = variants[variant];

    return (
      <div
        ref={ref}
        className={clsx(
          'rounded-lg border p-4',
          container,
          className
        )}
        role="alert"
        {...props}
      >
        <div className="flex items-start gap-3">
          <Icon className={clsx('h-5 w-5 flex-shrink-0 mt-0.5', iconColor)} />
          <div className="flex-grow">
            {title && (
              <h4 className="font-semibold mb-1">{title}</h4>
            )}
            <div className="text-sm">{children}</div>
          </div>
          {dismissible && onDismiss && (
            <button
              onClick={onDismiss}
              className="flex-shrink-0 p-1 hover:bg-black/5 rounded-md transition-colors"
              aria-label="Dismiss alert"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export { Alert };
